import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Header, Message, Tab } from 'semantic-ui-react';

import OnboardingQueueList from '../views/OnboardingQueue/List';
import OnboardingQueueFilterPanel from '../views/OnboardingQueue/FilterPanel';
import ActivateDialog from '../views/OnboardingQueue/ActivateDialog';
import BatchActivateDialog from '../views/OnboardingQueue/BatchActivateDialog';
import BatchDetailsDialog from '../views/OnboardingQueue/BatchDetailsDialog';
import EditStatusDialog from '../views/OnboardingQueue/EditStatusDialog';
import NotesDialog from '../views/OnboardingQueue/NotesDialog';
import OptOutDialog from '../views/OnboardingQueue/OptOutDialog';
import Pagination from '../views/Shared/Pagination';
import { loadQueue, reloadView, resetQueue } from '../data/onboardingQueue/actions';
import {
  activateGroup,
  activateRecord,
  addRecordNote,
  editRecordStatus,
  fetchRecordDetail,
  optOutGroup,
  optOutRecord,
  refetchActiveView,
  refetchBoth,
  settleActivePage,
} from '../data/onboardingQueue/recordActions';
import {
  selectCompletedTotal,
  selectFiltersActive,
  selectPendingTotal,
  selectQueueFilters,
  selectQueueLoaded,
  selectQueueLoading,
  selectQueueRefreshing,
  selectQueueView,
} from '../data/onboardingQueue/selectors';
import {
  ACTIVATE_SUCCESS_MESSAGE,
  DATE_FILTER_LABELS,
  EDIT_STATUS_SUCCESS_MESSAGE,
  GROUP_RESULT_VERB,
  NOTE_SUCCESS_MESSAGE,
  OPT_OUT_SUCCESS_MESSAGE,
  QUEUE_ACTION,
  QUEUE_PAGE_HEADER,
  QUEUE_PAGE_SIZE,
  QUEUE_VIEW,
  SORT_ORDER,
  VIEW_DEFAULT_ORDER,
  VIEW_SORT_KEY,
  VIEW_TAB_LABELS,
} from '../constants/onboardingQueue';
import { groupResultNotice, isBatchRow, pendingDealers } from '../utilities/onboardingQueue';

const VIEW_ORDER = [QUEUE_VIEW.PENDING, QUEUE_VIEW.COMPLETED];
const NO_FILTERS = { search: '', from: null, to: null };
const FIRST_PAGE = 0;
const INITIAL_VIEW_STATE = {
  [QUEUE_VIEW.PENDING]: { sort: null, order: null, page: FIRST_PAGE },
  [QUEUE_VIEW.COMPLETED]: { sort: null, order: null, page: FIRST_PAGE },
};

const REFETCH_SCOPE = { BOTH: 'both', VIEW: 'view' };

const opposite = order => (order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC);

const OnboardingQueue = () => {
  const dispatch = useDispatch();
  const [view, setView] = useState(QUEUE_VIEW.PENDING);
  const [draft, setDraft] = useState(NO_FILTERS);
  const [viewStates, setViewStates] = useState(INITIAL_VIEW_STATE);
  const [activeDialog, setActiveDialog] = useState(null);
  const [dialogRow, setDialogRow] = useState(null);
  const [dialogRecord, setDialogRecord] = useState(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);

  const filters = useSelector(selectQueueFilters);
  const loaded = useSelector(selectQueueLoaded);
  const loading = useSelector(selectQueueLoading);
  const refreshing = useSelector(selectQueueRefreshing);
  const filtersActive = useSelector(selectFiltersActive);
  const pendingTotal = useSelector(selectPendingTotal);
  const completedTotal = useSelector(selectCompletedTotal);
  const active = useSelector(state => selectQueueView(state, view));

  useEffect(() => {
    dispatch(loadQueue({ filters: NO_FILTERS, viewStates: INITIAL_VIEW_STATE }));
    return () => dispatch(resetQueue());
  }, [dispatch]);

  const { items, totalRows } = active;
  const { order, page } = viewStates[view];
  const isInitialLoading = loading || !loaded;
  const start = items.length > 0 ? page * QUEUE_PAGE_SIZE + 1 : 0;
  const end = start > 0 ? start + items.length - 1 : 0;
  const hasNext = end < totalRows;
  const isLastPage = !hasNext && items.length > 0;

  const totals = {
    [QUEUE_VIEW.PENDING]: pendingTotal,
    [QUEUE_VIEW.COMPLETED]: completedTotal,
  };

  const tabLabel = paneView => {
    const count = totals[paneView];
    const label = VIEW_TAB_LABELS[paneView];
    return !loaded || count === null ? label : `${label} (${count})`;
  };

  const onSearchChange = useCallback(value => {
    setDraft(current => ({ ...current, search: value }));
  }, []);

  const onFromChange = useCallback(value => {
    setDraft(current => {
      const from = value || null;
      if (from && current.to && from.isAfter(current.to, 'day')) return current;
      return { ...current, from };
    });
  }, []);

  const onToChange = useCallback(value => {
    setDraft(current => {
      const to = value || null;
      if (to && current.from && to.isBefore(current.from, 'day')) return current;
      return { ...current, to };
    });
  }, []);

  const onFilterSubmit = useCallback(() => {
    setViewStates(INITIAL_VIEW_STATE);
    dispatch(loadQueue({ filters: draft, viewStates: INITIAL_VIEW_STATE }));
  }, [dispatch, draft]);

  const onFilterClear = useCallback(() => {
    setDraft(NO_FILTERS);
    setViewStates(INITIAL_VIEW_STATE);
    dispatch(loadQueue({ filters: NO_FILTERS, viewStates: INITIAL_VIEW_STATE }));
  }, [dispatch]);

  const reload = useCallback((nextView, nextState) => {
    setViewStates(current => ({ ...current, [nextView]: nextState }));
    dispatch(reloadView({ filters, view: nextView, ...nextState }));
  }, [dispatch, filters]);

  const onSort = useCallback(() => {
    reload(view, {
      sort: VIEW_SORT_KEY[view],
      order: opposite(viewStates[view].order || VIEW_DEFAULT_ORDER[view]),
      page: FIRST_PAGE,
    });
  }, [reload, view, viewStates]);

  const onNextPage = useCallback(() => {
    if (hasNext) reload(view, { ...viewStates[view], page: viewStates[view].page + 1 });
  }, [reload, hasNext, view, viewStates]);

  const onPreviousPage = useCallback(() => {
    const current = viewStates[view];
    if (current.page > FIRST_PAGE) {
      reload(view, { ...current, page: current.page - 1 });
    }
  }, [reload, view, viewStates]);

  const onTabChange = useCallback((event, { activeIndex }) => {
    setView(VIEW_ORDER[activeIndex]);
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setDialogRow(null);
    setDialogRecord(null);
    setDialogError(null);
  }, []);

  const loadNotes = useCallback(async record => {
    setNotes([]);
    setNotesError(null);
    setNotesLoading(true);
    const result = await dispatch(fetchRecordDetail(record.flatRateOfferId));
    setNotesLoading(false);
    if (result.success) {
      setNotes((result.data && result.data.notes) || []);
    } else {
      setNotesError(result.message);
    }
  }, [dispatch]);

  const onAction = useCallback(async(action, row) => {
    const [record] = row.dealers;
    setActiveDialog(action);
    setDialogRow(row);
    setDialogRecord(record);
    setDialogError(null);
    setNotice(null);
    if (action === QUEUE_ACTION.NOTES) await loadNotes(record);
  }, [loadNotes]);

  const refetchAfterAction = useCallback(async scope => {
    const current = viewStates[view];
    if (scope === REFETCH_SCOPE.BOTH) {
      await dispatch(refetchBoth({ filters, viewStates }));
    } else {
      await dispatch(refetchActiveView({ filters, view, ...current }));
    }

    const settled = await dispatch(settleActivePage({ filters, view, ...current }));
    if (settled !== current.page) {
      setViewStates(states => ({ ...states, [view]: { ...states[view], page: settled } }));
    }
  }, [dispatch, filters, view, viewStates]);

  const runAction = useCallback(async(thunk, { notice: nextNotice, scope, closeOnSuccess }) => {
    setDialogBusy(true);
    setDialogError(null);
    const result = await dispatch(thunk);
    setDialogBusy(false);

    if (result.success) {
      setNotice(typeof nextNotice === 'function' ? nextNotice(result.data) : nextNotice);
      if (closeOnSuccess) closeDialog();
    } else {
      setDialogError(result.message);
    }

    await refetchAfterAction(scope);
    return result;
  }, [closeDialog, dispatch, refetchAfterAction]);

  const onActivateConfirm = useCallback(() => runAction(
    activateRecord({ flatRateOfferId: dialogRecord.flatRateOfferId }),
    { notice: { content: ACTIVATE_SUCCESS_MESSAGE }, scope: REFETCH_SCOPE.BOTH, closeOnSuccess: true }
  ), [dialogRecord, runAction]);

  const onBatchActivateConfirm = useCallback(flatRateOfferIds => runAction(
    activateGroup({ flatRateOfferIds }),
    {
      notice: data => groupResultNotice(data, {
        verb: GROUP_RESULT_VERB.ACTIVATE, dealers: dialogRow.dealers,
      }),
      scope: REFETCH_SCOPE.BOTH,
      closeOnSuccess: true,
    }
  ), [dialogRow, runAction]);

  const onOptOutConfirm = useCallback(({ reason, otherText }) => runAction(
    optOutRecord({ flatRateOfferId: dialogRecord.flatRateOfferId, reason, otherText }),
    { notice: { content: OPT_OUT_SUCCESS_MESSAGE }, scope: REFETCH_SCOPE.BOTH, closeOnSuccess: true }
  ), [dialogRecord, runAction]);

  const onBatchOptOutConfirm = useCallback(({ reason, otherText }) => {
    const dealers = pendingDealers(dialogRow);
    return runAction(
      optOutGroup({
        flatRateOfferIds: dealers.map(dealer => dealer.flatRateOfferId), reason, otherText,
      }),
      {
        notice: data => groupResultNotice(data, { verb: GROUP_RESULT_VERB.OPT_OUT, dealers }),
        scope: REFETCH_SCOPE.BOTH,
        closeOnSuccess: true,
      }
    );
  }, [dialogRow, runAction]);

  const onEditStatusConfirm = useCallback(({ status, reason, otherText }) => runAction(
    editRecordStatus({
      flatRateOfferId: dialogRecord.flatRateOfferId, status, reason, otherText,
    }),
    { notice: { content: EDIT_STATUS_SUCCESS_MESSAGE }, scope: REFETCH_SCOPE.BOTH, closeOnSuccess: true }
  ), [dialogRecord, runAction]);

  const onNotesDealerChange = useCallback(async dealer => {
    setDialogRecord(dealer);
    setDialogError(null);
    await loadNotes(dealer);
  }, [loadNotes]);

  const onAddNoteConfirm = useCallback(async({ note }) => {
    const result = await runAction(
      addRecordNote({ flatRateOfferId: dialogRecord.flatRateOfferId, note }),
      { notice: { content: NOTE_SUCCESS_MESSAGE }, scope: REFETCH_SCOPE.VIEW, closeOnSuccess: false }
    );
    if (result.success && result.data) setNotes(result.data.notes || []);
    return result;
  }, [dialogRecord, runAction]);

  const panes = VIEW_ORDER.map(paneView => ({
    menuItem: {
      key: paneView,
      content: tabLabel(paneView),
      'data-testid': `queue-tab-${paneView}`,
    },
    render: () => (
      <Tab.Pane>
        <OnboardingQueueList
          view={view}
          items={items}
          totalRows={totalRows}
          loading={isInitialLoading}
          error={active.error}
          refused={active.refused}
          filtersActive={filtersActive}
          sortOrder={order || VIEW_DEFAULT_ORDER[view]}
          onSort={onSort}
          onAction={onAction}
        />

        {items.length > 0 && (
          <Pagination
            start={start}
            end={end}
            nextHandler={onNextPage}
            previousHandler={onPreviousPage}
            loading={refreshing}
            lastPage={isLastPage}
            pageSize={QUEUE_PAGE_SIZE}
          />
        )}
      </Tab.Pane>
    ),
  }));

  return (
    <div data-testid='onboarding-queue'>
      <Header size='large'>{QUEUE_PAGE_HEADER}</Header>

      {notice && (
        <Message
          positive={!notice.warning}
          warning={Boolean(notice.warning)}
          data-testid='queue-action-success'
          header={notice.list ? notice.content : undefined}
          content={notice.list ? undefined : notice.content}
          list={notice.list || undefined}
          onDismiss={() => setNotice(null)}
        />
      )}

      <OnboardingQueueFilterPanel
        searchValue={draft.search}
        from={draft.from}
        to={draft.to}
        dateLabel={DATE_FILTER_LABELS[view]}
        onSearchChange={onSearchChange}
        onFromChange={onFromChange}
        onToChange={onToChange}
        onSubmit={onFilterSubmit}
        onClear={onFilterClear}
      />

      <Tab
        panes={panes}
        activeIndex={VIEW_ORDER.indexOf(view)}
        onTabChange={onTabChange}
      />

      {activeDialog === QUEUE_ACTION.ACTIVATE && isBatchRow(dialogRow) && (
        <BatchActivateDialog
          dealers={dialogRow.dealers}
          loading={dialogBusy}
          errorMessage={dialogError}
          onClose={closeDialog}
          onConfirm={onBatchActivateConfirm}
        />
      )}

      {activeDialog === QUEUE_ACTION.SEE_DETAILS && (
        <BatchDetailsDialog
          view={view}
          dealers={dialogRow.dealers}
          onClose={closeDialog}
          onDealerAction={onAction}
        />
      )}

      {activeDialog === QUEUE_ACTION.ACTIVATE && !isBatchRow(dialogRow) && (
        <ActivateDialog
          record={dialogRecord}
          loading={dialogBusy}
          errorMessage={dialogError}
          onClose={closeDialog}
          onConfirm={onActivateConfirm}
        />
      )}

      {activeDialog === QUEUE_ACTION.OPT_OUT && (
        <OptOutDialog
          record={dialogRecord}
          dealers={isBatchRow(dialogRow) ? pendingDealers(dialogRow) : null}
          loading={dialogBusy}
          errorMessage={dialogError}
          onClose={closeDialog}
          onConfirm={isBatchRow(dialogRow) ? onBatchOptOutConfirm : onOptOutConfirm}
        />
      )}

      {activeDialog === QUEUE_ACTION.EDIT_STATUS && (
        <EditStatusDialog
          record={dialogRecord}
          loading={dialogBusy}
          errorMessage={dialogError}
          onClose={closeDialog}
          onConfirm={onEditStatusConfirm}
        />
      )}

      {activeDialog === QUEUE_ACTION.NOTES && (
        <NotesDialog
          key={dialogRecord.flatRateOfferId}
          record={dialogRecord}
          dealers={isBatchRow(dialogRow) ? dialogRow.dealers : null}
          onDealerChange={isBatchRow(dialogRow) ? onNotesDealerChange : null}
          notes={notes}
          notesLoading={notesLoading}
          notesError={notesError}
          loading={dialogBusy}
          errorMessage={dialogError}
          onClose={closeDialog}
          onConfirm={onAddNoteConfirm}
        />
      )}
    </div>
  );
};

export default OnboardingQueue;
